import React from 'react';
import { ChildProps, withApollo, WithApolloClient } from 'react-apollo';
import I18n from 'i18n-js';
import { get, some, differenceBy, merge } from 'lodash';
import { browserHistory, withRouter, WithRouterProps } from 'react-router';
import { Location } from 'history';

import {
  combinedMarketplaceSearchQuery,
  mapUrlParams,
  PageSettings,
  includePromotedRows,
  onlyQuery,
  usingSignalSystemMarketplace,
} from './map_params';
import { getPage, getTotalPages, getOffsetFromPage } from '../../pagination_helpers';
import {
  CoreMarketplaceCombinedMarketplaceSearch,
  Ad,
  reverb_search_ListingsSearchRequest_Sort,
  reverb_signals_Signal_Group,
  reverb_personalization_SearchQuery,
} from '../../schemas/rql';
import FollowButtonContainer from '../discovery/feed/follow_button_container';
import MarketplaceCSPRow from '../discovery/csp/marketplace_csp_row';
import SidebarAds from '../discovery/facets/sidebar_ads';
import { MarketplacePathContext, Paths } from '../shared/url_helpers';
import CurationModal from '../discovery/curation/modal';
import HeaderWithMetadata from './header_with_metadata';
import ListingsWithBumps from './listings_with_bumps';
import Listings from './listings';
import { applyAutodirect, AUTODIRECTS_GROUP, autodirectUrl, autoselectedFilters, contexts, updateMarketplaceUrl } from './marketplace_autodirects';
import { pageViewTrackedExperiments } from './page_view_tracking_data';
import { listingsFilters } from '../listings_filters';
import MarketplaceFallbackListings from './marketplace_fallback_listings';
import LowResultsAlert from '../low_results_alert';
import NoResultsAlert from '../no_results_alert';
import { SubqueryRows } from '../subquery_rows';

import RQLSearchGrid from '@reverbdotcom/commons/src/components/rql_search_grid';
import ScrollTarget from '@reverbdotcom/commons/src/components/scroll_target';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { experimentInt, isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import { UpdateURLProvider } from '@reverbdotcom/commons/src/components/with_update_url';
import { MParticlePageName, MParticleEventName, trackPageView, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { adBannerMparticleData } from '@reverbdotcom/commons/src/components/ads/ad_mparticle_tracking_helpers';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { pageShouldShowEmailPopUp, addOnExitTriggerForEmailPopUp, EmailPopUpModal } from '@reverbdotcom/commons/src/email_pop_up_helpers';
import { isBrandPage, isUsingProximityFeatures } from '@reverbdotcom/commons/src/url_helpers';
import { BRAND_NEW } from '@reverbdotcom/commons/src/condition_helpers';
import * as elog from '@reverbdotcom/commons/src/elog';
import { AdBanner } from '@reverbdotcom/commons/src/components/ads/ad_banner';
import { trackCriteoProductList } from '@reverbdotcom/commons/src/criteo';
import bind from '@reverbdotcom/commons/src/bind';
import { setItem, getItem } from '@reverbdotcom/commons/src/local_storage_utils';
import { isGreatValue } from '@reverbdotcom/commons/src/listing_great_value';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import experiments from '@reverbdotcom/commons/src/experiments';
import MarketplaceQuerySuggestions from '../discovery/query_suggestions';
import withQuerySuggestions from '../with_query_suggestions';

interface RouterLocationProps {
  location: Location;
  params: MarketplacePathContext;
}

interface IExternalProps extends RouterLocationProps {
  emptyGrid?: JSX.Element;
  hideFollowButtons?: boolean;
  pageSettings: PageSettings;
  renderCollectionHeaderDescription?: boolean;
  skipHeader?: boolean;
  pageName?: MParticlePageName;
  ad?: Ad;
  loading?: boolean;
  querySuggestions?: reverb_personalization_SearchQuery[];
}

interface IState {
  listViewEnabled: boolean;
  openModal: boolean;
}

type ICombinedProps = IUserContext & WithRouterProps & WithApolloClient<IExternalProps>;

export type MPProps = ChildProps<ICombinedProps, CoreMarketplaceCombinedMarketplaceSearch.Query, CoreMarketplaceCombinedMarketplaceSearch.Variables>;

const BUMP_LIMIT = 15;
const LOW_LISTINGS_THRESHOLD = 10;
export const LIST_VIEW_STATE = 'LIST_VIEW_STATE';
export const LAST_SEARCH_URL = 'last_search_url';

export class MarketplacePage extends React.Component<MPProps, IState> {
  state: IState = { listViewEnabled: getItem(LIST_VIEW_STATE), openModal: false };

  scrollTarget: ScrollTarget;

  @bind
  setListViewState(enabled: boolean) {
    setItem(LIST_VIEW_STATE, enabled);
    this.setState(
      { listViewEnabled: enabled },
      () => this.props.data.refetch({
        usingListView: enabled,
        shouldntLoadBumps: false,
        shouldntLoadSuggestions: false,
        useSignalSystem: usingSignalSystemMarketplace(this.props.user),
        signalGroups: getSignalGroups(this.props.location, enabled),
      }),
    );
  }

  @bind
  setOpenModal(isOpen: boolean) {
    this.setState({ openModal: isOpen });
  }

  componentDidMount() {
    if (this.isMarketplacePage()) {
      this.trackAdExperimentQualifyingEvent();
    }

    this.addEmailPopUpTriggerIfAllowedPage();
  }

  componentDidUpdate(prevProps: MPProps) {
    if (prevProps.location.query.page !== this.props.location.query.page) {
      this.scrollTarget.scrollToTop();
    }

    if (prevProps.querySuggestions !== this.props.querySuggestions) {
      this.trackQuerySuggestions();
      if (prevProps.querySuggestions?.length) {
        this.scrollTarget.scrollToTop();
      }
    }

    if (this.loading()) { return; }

    if (prevProps.data.listingsSearch !== this.props.data.listingsSearch) {
      this.trackPageView();
      if (this.isMarketplacePage()) {
        this.trackAdExperimentQualifyingEvent();
      }
    }

    this.setLastSearch(prevProps);

    applyAutodirect({ prevProps, props: this.props });

    if (this.isBrandModelRedirected()) {
      this.stripRedirectParam('brand_model_redirected');
    }

    if (this.isBrandRedirected()) {
      this.stripRedirectParam('brand_redirected');
    }
  }

  setLastSearch(prevProps: MPProps) {
    if (this.isMarketplacePage()) {
      if (this.getQuery(prevProps) === this.getQuery(this.props)) {
        return;
      }

      setItem(LAST_SEARCH_URL, Paths.marketplace.expand({ query: this.props.location.query.query }));
    } else {
      setItem(LAST_SEARCH_URL, this.props.location.pathname);
    }
  }

  stripRedirectParam(param) {
    const { location } = this.props;
    delete location.query[param];
    browserHistory.push(location);
  }

  isMarketplacePage() {
    return this.props.pageName === MParticlePageName.Marketplace;
  }

  isBrandRedirected() {
    return this.props.location.query.brand_redirected === 'true';
  }

  isBrandModelRedirected() {
    return this.props.location.query.brand_model_redirected === 'true';
  }

  loading() {
    return this.props.loading || !this.props.data || this.props.data.loading;
  }

  searchResult(): CoreMarketplaceCombinedMarketplaceSearch.ListingsSearch {
    return this.props.data?.listingsSearch;
  }

  bumpSearchResult(): CoreMarketplaceCombinedMarketplaceSearch.ListingsSearch {
    return this.props.data?.bumpedSearch;
  }

  filters() {
    const filters = this.props.data?.aggsSearch?.filters || [];
    return listingsFilters(filters, this.props.user, this.props.location);
  }

  getQuery(props: MPProps) {
    return props.data?.aggsSearch?.filters.find(f => f.key === 'TEXT_QUERY')?.options[0]?.optionValue;
  }

  getTotalText() {
    const count = get(this.props, 'data.listingsSearch.total', 0);

    return I18n.t(
      'discovery.resultsCount.countListings',
      {
        count,
        num: I18n.toNumber(count, { precision: 0 }),
      },
    );
  }

  getFollowParams() {
    return { ...this.props.params, ...this.props.location.query };
  }

  getListings() {
    return this.searchResult()?.listings || [];
  }

  getBumpedListings() {
    return this.bumpSearchResult()?.listings || [];
  }

  defaultSort() {
    let defaultSort;
    const filters = this.filters();
    const sortFilter = filters.find(({ key }) => key === 'SORT_BY');
    if (sortFilter) {
      defaultSort = sortFilter.options.find(({ selected }) => !!selected)?.trackingValue;
    }
    return defaultSort || reverb_search_ListingsSearchRequest_Sort.NONE;
  }

  getFallbackListings() {
    const fallback = this.searchResult()?.fallbackListings || [];
    const listings = this.getListings();

    if (listings.length === 0) return fallback;

    return differenceBy(fallback, listings, 'id');
  }

  getSuggestedQuery() {
    const queries = this.searchResult()?.suggestedQueries || [];

    return queries?.[0];
  }

  shouldShowFallbackListings() {
    return this.getFallbackListings().length > 0;
  }

  hideProductRow() {
    if (this.props.pageSettings.holidaySale) return true;
    if (this.props.params.sale_slug) return true;

    return some([
      'on_sale',
      'handmade',
      'free_shipping',
      'free_expedited_shipping',
      'show_only_sold',
      'accepts_offers',
      'accepts_gift_cards',
      'preferred_seller',
      'zero_percent_financing',
      'csp_slug',
      'combined_shipping',
    ], (item) => {
      return this.props.location.query[item];
    });
  }

  renderRelatedSearchRow() {
    if (this.hideProductRow()) {
      return null;
    }

    const expGroup = experimentInt(this.props.user, experiments.MARKETPLACE_QUERY_SUGGESTIONS);
    if (expGroup === 1 && this.isMarketplacePage() && !!this.props.location.query.query) {
      return <MarketplaceQuerySuggestions querySuggestions={this.props.querySuggestions} loading={this.loading()} />;
    }

    return (
      <MarketplaceCSPRow
        title={I18n.t('discovery.cspRowTitle')}
        location={this.props.location}
        routeParams={this.props.params}
        trackingQuery={this.trackingQuery()}
        autodirect={autodirectUrl(this.props)}
      />
    );
  }

  renderSecondaryRelatedSearchRow() {
    if (this.hideProductRow() || !this.isMarketplacePage() || !this.props.location.query.query) {
      return null;
    }

    const expGroup = experimentInt(this.props.user, experiments.MARKETPLACE_QUERY_SUGGESTIONS);
    if (expGroup === 1) {
      return (
        <MarketplaceCSPRow
          title={I18n.t('discovery.cspRowTitle')}
          location={this.props.location}
          routeParams={this.props.params}
          trackingQuery={this.trackingQuery()}
          autodirect={autodirectUrl(this.props)}
        />
      );
    }

    if (expGroup === 2) {
      return <MarketplaceQuerySuggestions querySuggestions={this.props.querySuggestions} loading={this.loading()} />;
    }

    return null ;
  }

  renderFollowBanner() {
    if (this.props.hideFollowButtons) return null;
    if (this.loading()) return null;
    if (this.searchResult() && !this.searchResult().total) return null;
    if (this.shouldShowFallbackListings()) return null;

    return (
      <FollowButtonContainer
        followParams={this.getFollowParams()}
        renderWithBanner
        disabled={!!this.props.location.query.trait_values}
        ignoreFollowPrompt
      />
    );
  }

  renderAdBanner() {
    const hasData = (!this.props.loading && !!this.props.ad?.imageAdCreative);
    if (!hasData) return null;
    // do not show ad banner if Shop Page
    if (this.props.pageName === MParticlePageName.Shop) return null;
    // do not show on Marketplace Page if user is in MARKETPLACE_GRID_ADS exp
    if (isExperimentEnabled(this.props.user, experiments.MARKETPLACE_GRID_ADS) && this.isMarketplacePage()) return null;

    return (
      <div className="mt-3 mb-2 site-wrapper">
        <AdBanner
          ad={this.props.ad}
        />
      </div>
    );
  }

  shouldShowCombinedListings() {
    if (this.shouldShowFallbackListings()) return null;
    if (this.getListings().length === 0) return null;
    if (this.hasSort()) return null;

    return includePromotedRows(this.props.params, this.props.location?.query);
  }

  didYouMeanSuggestion() {
    return this.searchResult()?.suggestions[0];
  }

  useExperimentalRecall() {
    return this.props.pageSettings.useExperimentalRecall;
  }

  emptyGridExperimentOverride() {
    if (this.useExperimentalRecall() && onlyQuery(this.props.location.query)) {
      return (
        <NoResultsAlert
          query={this.props.location?.query.query}
          suggestion={this.didYouMeanSuggestion()}
        />
      );
    }

    // overrides default empty grid if either experiment is active
    if (this.shouldShowFallbackListings()) return <div />;

    // uses default
    return this.props.emptyGrid;
  }

  hasLowResults() {
    return !this.props.data?.loading &&
      this.getListings().length <= LOW_LISTINGS_THRESHOLD;
  }

  hasNoResults() {
    return !this.props.data?.loading &&
      this.getListings().length === 0;
  }

  hasSort() {
    return !!this.props.location?.query?.sort;
  }

  wasReferredByTrendingSearches() {
    return this.props.location.query.referrer === 'trending_search';
  }

  lowResultsAlert() {
    if (this.wasReferredByTrendingSearches()) { return null; }
    if (onlyQuery(this.props.location.query) &&
        this.hasLowResults() &&
        !this.hasNoResults() &&
        this.useExperimentalRecall()) {
      return (
        <LowResultsAlert
          suggestion={this.didYouMeanSuggestion()}
          query={this.props.location?.query.query}
        />
      );
    }

    return null;
  }

  gridAd(): Ad {
    if (isExperimentEnabled(this.props.user, experiments.MARKETPLACE_GRID_ADS) && this.isMarketplacePage()) {
      return this.props.ad;
    }
    return null;
  }

  renderCards() {
    if (this.shouldShowCombinedListings()) {
      return (
        <ListingsWithBumps
          useListView={this.state.listViewEnabled}
          error={this.props.data.error}
          listings={this.getListings()}
          bumpedListings={this.getBumpedListings()}
          showOnlySold={this.props.location.query.showOnlySold}
          loading={this.loading()}
          trackingQuery={this.trackingQuery()}
          ad={this.gridAd()}
          pageSettings={this.props.pageSettings}
        />
      );
    }

    return (
      <Listings
        useListView={this.state.listViewEnabled}
        loading={this.loading()}
        error={!!this.props.data.error}
        listings={this.getListings()}
        showingSold={this.props.location.query.show_only_sold}
        emptyGrid={this.emptyGridExperimentOverride()}
        trackingQuery={this.trackingQuery()}
        pageSettings={this.props.pageSettings}
      />
    );
  }

  renderFallbackCards() {
    return (
      <MarketplaceFallbackListings
        fallbackListings={this.getFallbackListings()}
        query={this.props.location.query.query}
        hasPrimaryListings={this.getListings().length > 0}
        showingSold={this.props.location.query.show_only_sold}
        displayAsRow={this.useExperimentalRecall()}
      />
    );
  }

  renderSubqueryRows() {
    if (this.wasReferredByTrendingSearches()) { return null; }
    if (!this.useExperimentalRecall() ||
      onlyQuery(this.props.location.query) ||
      !this.hasLowResults()) return null;

    const { query } = this.props.location.query;

    return (
      <SubqueryRows
        query={query}
        location={this.props.location}
        params={this.props.params}
        listingsSearchCount={this.getListings().length}
      />
    );
  }

  trackingQuery() {
    return this.isBrandRedirected() ? this.props.location.query.make : this.props.location.query.query;
  }

  freeShippingListingIds() {
    const listings = this.getListings();
    return listings.filter(listing => listing.shipping?.shippingPrices?.[0]?.rate?.amountCents === 0).map(l => l.id);
  }

  greatValueListingIds() {
    const listings = this.getListings();
    return listings.filter(listing => isGreatValue(listing, this.props.user)).map(l => l.id);
  }

  returnPolicyListingIds() {
    const listings = this.getListings();
    return listings.filter(listing =>  {
      if (listing.soldAsIs) { return; }
      const returnPolicyDays = listing.condition.conditionSlug === BRAND_NEW ? listing.shop?.returnPolicy?.newReturnWindowDays : listing.shop?.returnPolicy?.usedReturnWindowDays;
      return !!returnPolicyDays && returnPolicyDays >= 14;
    }).map(l => l.id);
  }

  sharedTrackingData() {
    const { listViewEnabled } = this.state;
    const results = this.searchResult();
    const trackedExperiments = pageViewTrackedExperiments(results);

    const trackingData = {
      pageName: this.props.pageName || MParticlePageName.Marketplace,
      experiments: formatExperimentsForEventAttributes(this.props.user, trackedExperiments),
      query: this.trackingQuery(),
      autodirected: autoselectedFilters(this.props).length !== 0,
      brandRedirected: this.isBrandRedirected(),
      brandModelRedirected: this.isBrandModelRedirected(),
      sort: this.props.location?.query?.sort || this.defaultSort(),
      listingIds: results.listings?.map(l => l.id),
      querySuggestion: this.getSuggestedQuery(),
      page: getPage(results.offset, results.limit),
      totalPages: getTotalPages(results.total, results.limit),
      listingsCount: results.total,
      fallbackListingsCount: this.getFallbackListings().length,
      productType: this.props.location.query.product_type || '',
      make: this.props.location.query.make || '',
      category: this.props.location.query.category || '',
      shopSlug: this.props.params.shop || '',
      brand: this.props.params.brand_slug || '',
      curatedSetSlug: this.props.params.curated_set_slug || '',
      postalCode: this.props.location.query.postal_code,
      distance: this.props.location.query.distance,
      view: listViewEnabled ? 'List' : 'Grid',
      freeShippingListingIds: this.freeShippingListingIds(),
      returnPolicyListingIds: this.returnPolicyListingIds(),
      refererSlug: this.props.location.query.referer,
    };

    if (this.props.user.shippingRegionCode === 'GB' && this.greatValueListingIds().length > 0) {
      merge(trackingData, { greatValueListingIds: this.greatValueListingIds() });
    }

    if (this.shouldShowCombinedListings()) {
      merge(trackingData, { bumpedListingIds: this.getBumpedListings()?.map(l => l.id) });
    }

    return trackingData;
  }

  trackPageView() {
    trackPageView({
      ...this.sharedTrackingData(),
    });

    const results = this.searchResult();

    trackCriteoProductList({
      listingIds: results.listings.map(l => l.id),
      keywords: this.trackingQuery(),
      page_number: getPage(results.offset, results.limit),
    }, this.props.user);
  }

  trackQuerySuggestions() {
    const { user, querySuggestions } = this.props;

    querySuggestions.forEach(suggestion => {
      trackEvent({
        eventName: MParticleEventName.ViewedQuerySuggestion,
        experiments: formatExperimentsForEventAttributes(user, [experiments.MARKETPLACE_QUERY_SUGGESTIONS]),
        make: suggestion.make,
        category: suggestion.category,
        productType: suggestion.productType,
        query: suggestion.query,
      });
    });
  }

  trackAdExperimentQualifyingEvent() {
    trackEvent({
      ...adBannerMparticleData(this.props.ad),
      ...{
        componentName: MParticlePageName.Marketplace,
        eventName: MParticleEventName.MarketplaceGridAdQE,
        experiments: formatExperimentsForEventAttributes(this.props.user, [experiments.MARKETPLACE_GRID_ADS]),
      },
    });
  }

  @bind
  addEmailPopUpTriggerIfAllowedPage() {
    if (pageShouldShowEmailPopUp(this.props.location)) {
      addOnExitTriggerForEmailPopUp(this.props.user, this.state.openModal, this.setOpenModal);
    }
  }

  renderSellerLocationFilter(): boolean {
    const { pathname } = this.props.location;
    return pathname && !pathname.includes('/shop');
  }

  render() {
    const searchResult = this.searchResult();

    if (this.props.data.error) {
      elog.error(
        'marketplace-render-failure',
        {
          networkErrorStack: this.props.data.error.networkError?.stack?.substring(0, 500),
        },
        this.props.data.error,
      );
    }

    return (
      <UpdateURLProvider updateUrl={updateMarketplaceUrl}>
        {!this.props.skipHeader &&
          <HeaderWithMetadata
            params={this.props.params}
            query={get(this.props, 'location.query', {})}
            pageSettings={this.props.pageSettings}
            renderCollectionHeaderDescription={this.props.renderCollectionHeaderDescription}
            isBrandPage={isBrandPage(this.props.location)}
          />
        }
        <ScrollTarget onlyScrollUp ref={r => this.scrollTarget = r} />
        <RQLSearchGrid
          error={!!this.props.data.error}
          loading={this.loading()}
          search={searchResult}
          filters={this.filters()}
          location={this.props.location}
          aboveGridBanner={this.renderAdBanner()}
          relatedSearchRow={this.renderRelatedSearchRow()}
          secondaryRelatedSearchRow={this.renderSecondaryRelatedSearchRow()}
          totalText={this.getTotalText()}
          followButton={!this.props.hideFollowButtons &&
            <FollowButtonContainer
              followParams={this.getFollowParams()}
              disabled={!!this.props.location.query.trait_values}
            />
          }
          cards={this.renderCards()}
          fallbackCards={this.renderFallbackCards()}
          subqueryRows={this.renderSubqueryRows()}
          hasPagination
          hasFacets
          hasSearchOverview
          sidebarContent={<SidebarAds region={this.props.user.shippingRegionCode} />}
          followBanner={this.renderFollowBanner()}
          renderSellerLocationFilter={this.renderSellerLocationFilter()}
          trackingQuery={this.trackingQuery()}
          lowResultsAlert={this.lowResultsAlert()}
          setListViewState={this.setListViewState}
          showListViewToggle
        />
        <CurationModal />
        <EmailPopUpModal
          openModal={this.state.openModal}
          setOpenModal={this.setOpenModal}
          location={this.props.location}
        />
      </UpdateURLProvider>
    );
  }
}

function getSignalGroups(location, usingListView) {
  const useProximitySignals = isUsingProximityFeatures(location);
  const signalGroups = [];
  if (usingListView) {
    signalGroups.push(useProximitySignals ? reverb_signals_Signal_Group.MP_LIST_PROX_CARD : reverb_signals_Signal_Group.MP_LIST_CARD);
  } else {
    signalGroups.push(useProximitySignals ? reverb_signals_Signal_Group.MP_GRID_PROX_CARD : reverb_signals_Signal_Group.MP_GRID_CARD);
  }

  return signalGroups;
}

export function mpArgs(props: MPProps) {
  const usingListView = getItem(LIST_VIEW_STATE);
  const adTilesCount = (
    props.ad && isExperimentEnabled(props.user, experiments.MARKETPLACE_GRID_ADS) && this.isMarketplacePage()
  ) ? 1 : 0;

  return {
    ...mapUrlParams(
      props.params,
      props.location,
      props.user,
      {
        holidaySale: props.pageSettings.holidaySale,
        showSold: props.pageSettings.showSold,
        useExperimentalQuery: isExperimentEnabled(props.user, experiments.LISTINGS_QUERY_REWRITE_V2),
        useExperimentalRecall: props.pageSettings.useExperimentalRecall,
        usingListView,
        pageName: props.pageName,
      },
      adTilesCount,
    ),
    boostByBumpRate: !props.location?.query?.sort,
    bumpLimit: BUMP_LIMIT,
    bumpOffset: getOffsetFromPage(props.location.query.page, BUMP_LIMIT),
    contexts: contexts(props.location),
    autodirects: AUTODIRECTS_GROUP,
    sort: reverb_search_ListingsSearchRequest_Sort.NONE,
    signalGroups: getSignalGroups(props.location, usingListView),
  };
}

const connect = withGraphql<
ICombinedProps,
CoreMarketplaceCombinedMarketplaceSearch.Query,
CoreMarketplaceCombinedMarketplaceSearch.Variables
>(combinedMarketplaceSearchQuery, {
  options: (props) => {
    return {
      context: {
        headers: {
          'X-Item-Region': props.params.shop ? 'XX' : undefined,
        },
      },
      ssr: false,
      variables: mpArgs(props),
    };
  },
});

export default withUserContext<IExternalProps>(withApollo(withQuerySuggestions(withRouter(connect(MarketplacePage)))));
